/**
 * Plural forms for the languages with more than two (2026-10-31). vue-i18n alone picks "one | other" by `n === 1`,
 * which gives Polish "2 osób" (it is "2 osoby", "5 osób") and Croatian "21 osoba" from the wrong form. A message
 * written with this language's full set of forms - in the order below - gets the form `Intl.PluralRules` names for
 * the count; a message with any other number of forms (the two-form ones written before) keeps vue-i18n's own choice,
 * so nothing written earlier changes.
 *
 * Forms per message, in order (CLDR categories; a fraction's category falls to the last form):
 * - pl: one (1) | few (2–4, 22–24…) | many (0, 5–21, 25…)
 * - cz, sk: one (1) | few (2–4) | other (0, 5…)
 * - hr, sr: one (1, 21, 31… not 11) | few (2–4, 22–24… not 12–14) | other
 * - lt: one (1, 21… not 11) | few (2–9, 22–29… not 12–19) | other (0, 10–20, 30…)
 * - lv: zero (0, 10–20, 30…) | one (1, 21… not 11) | other
 * - ro: one (1) | few (0, 2–19, 101–119…) | other (20 and up: "20 de zile")
 * - sl: one (1, 101…) | two (2, 102…) | few (3–4, 103–104…) | other
 */
import type { PluralizationRule } from 'vue-i18n'

/** The app's locale code → its `Intl` tag and the categories of its forms, in message order. */
const FORMS: Record<string, { tag: string; order: readonly Intl.LDMLPluralRule[] }> = {
  pl: { tag: 'pl', order: ['one', 'few', 'many'] },
  cz: { tag: 'cs', order: ['one', 'few', 'other'] },
  sk: { tag: 'sk', order: ['one', 'few', 'other'] },
  hr: { tag: 'hr', order: ['one', 'few', 'other'] },
  sr: { tag: 'sr-Latn', order: ['one', 'few', 'other'] },
  lt: { tag: 'lt', order: ['one', 'few', 'other'] },
  lv: { tag: 'lv', order: ['zero', 'one', 'other'] },
  ro: { tag: 'ro', order: ['one', 'few', 'other'] },
  sl: { tag: 'sl', order: ['one', 'two', 'few', 'other'] },
}

/** vue-i18n's own choice (its default rule): "one | other" by 1, "zero | one | many" by 0, 1, more. */
function defaultForm(choice: number, choicesLength: number): number {
  const count = Math.abs(choice)
  if (choicesLength === 2) return count === 1 ? 0 : 1
  return count ? Math.min(count, 2) : 0
}

function ruleFor(tag: string, order: readonly Intl.LDMLPluralRule[]): PluralizationRule {
  const rules = new Intl.PluralRules(tag)
  return (choice, choicesLength) => {
    if (choicesLength !== order.length) return defaultForm(choice, choicesLength)
    const index = order.indexOf(rules.select(Math.abs(choice)))
    return index === -1 ? order.length - 1 : index
  }
}

/** How many forms a full plural message of `locale` has (2 for the languages not listed: "one | other"). */
export function pluralFormCount(locale: string): number {
  return FORMS[locale]?.order.length ?? 2
}

/** `pluralRules` of `createI18n`: one rule per language with more than two forms. */
export const PLURAL_RULES: Record<string, PluralizationRule> = Object.fromEntries(
  Object.entries(FORMS).map(([locale, { tag, order }]) => [locale, ruleFor(tag, order)]),
)
